import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { createUseStyles, ThemeProvider } from "react-jss";

import locale from "../locale";
import DeviceCategory from "../components/DeviceCategory";
import CalculatorResults from "./CalculatorResults";
import NotebookIcon from "./../assets/icons/laptop/PNG-laptop.png";
import PeripheralDeviceIcon from "../assets/icons/keyboard/PNG-keyboard.png";
import SmartphoneIcon from "../assets/icons/phone/PNG-phone.png";
import TabletIcon from "../assets/icons/tablet/PNG-tablet.png";
import DesktopIcon from "../assets/icons/computer/PNG-computer.png";
import AllInOneIcon from "../assets/icons/all-in-one-desktop/PNG-all-in-one-desktop.png";
import MonitorIcon from "../assets/icons/screen/PNG-screen.png";
import CalculatorIcon from "../assets/icons/calculator/PNG-calculator.png";
import ResetIcon from "../assets/icons/reset/PNG-reset.png";

import DustinSansEOT from "../assets/fonts/DustinSansWeb/eot/DustinSansWeb-Regular.eot";
import DustinSansWOFF from "../assets/fonts/DustinSansWeb/woff/DustinSansWeb-Regular.woff";
import DustinSansWOFF2 from "../assets/fonts/DustinSansWeb/woff2/DustinSansWeb-Regular.woff2";
import DustinSansBoldEOT from "../assets/fonts/DustinSansWeb/eot/DustinSansWeb-Bold.eot";
import DustinSansBoldWOFF from "../assets/fonts/DustinSansWeb/woff/DustinSansWeb-Bold.woff";
import DustinSansBoldWOFF2 from "../assets/fonts/DustinSansWeb/woff2/DustinSansWeb-Bold.woff2";
import DustinSansLightEOT from "../assets/fonts/DustinSansWeb/eot/DustinSansWeb-Light.eot";
import DustinSansLightWOFF from "../assets/fonts/DustinSansWeb/woff/DustinSansWeb-Light.woff";
import DustinSansLightWOFF2 from "../assets/fonts/DustinSansWeb/woff2/DustinSansWeb-Light.woff2";

const theme = {
  dustinDark: "#393939",
  dustinDarker: "#303030",
  dustinLight: "#F0F0F0",
  dustinSemiLight: "#E0E0E0",
  dustinGreen: "#4EF09D",
  dustinBorderDivider: "#434343",
};

const useStyles = createUseStyles({
  "@font-face": [
    {
      fontFamily: "DustinSans",
      fontWeight: "normal",
      fontStyle: "normal",
      src: `url(${DustinSansEOT}), url(${DustinSansWOFF}) format("woff"), url(${DustinSansWOFF2}) format("woff2")`,
    },
    {
      fontFamily: "DustinSans",
      fontWeight: "bold",
      fontStyle: "normal",
      src: `url(${DustinSansBoldEOT}), url(${DustinSansBoldWOFF}) format("woff"), url(${DustinSansBoldWOFF2}) format("woff2")`,
    },
    {
      fontFamily: "DustinSans",
      fontWeight: "lighter",
      fontStyle: "normal",
      src: `url(${DustinSansLightEOT}), url(${DustinSansLightWOFF}) format("woff"), url(${DustinSansLightWOFF2}) format("woff2")`,
    },
  ],
  calculatorRoot: {
    fontFamily: "DustinSans",
  },
  calculatorGrid: {
    margin: "auto",
    "@media (max-width: 991px)": {
      marginLeft: 16,
      marginRight: 16,
    },
    "@media (min-width: 992px)": {
      width: "100%",
      maxWidth: "970px",
    },
    "@media (min-width: 1200px)": {
      width: "100%",
      maxWidth: "1170px",
    },
  },
  calculatorInputs: {
    marginTop: 16,
    marginBottom: 16,
  },
  calculatorDevices: {
    display: "flex",
    flexWrap: "wrap",
  },
  calculatorCTARow: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: 24,
    marginBottom: 24,
  },
  calculatorIcon: {
    width: 22,
    height: 22,
  },
  calculateCTALabel: {
    fontSize: 16,
    color: "#2E2256",
  },
  calculateCTA: {
    margin: "8px !important",
    fontFamily: "DustinSans",
    height: "48px !important",
    width: "272px !important",
    padding: "16px !important",
    border: "none !important",
    borderRadius: "4px !important",
    backgroundColor: "#C48DF7 !important",
    display: "flex !important",
    justifyContent: "space-between !important",
    alignItems: "center !important",
    "&:hover": {
      cursor: "pointer !important",
      backgroundColor: "#ba79f6 !important",
    },
    "&:active": {
      backgroundColor: "#ad62f4 !important",
    },
    "&:disabled": {
      backgroundColor: "#D0D0D0 !important",
    },
  },
  resetIcon: {
    width: 16,
    height: 16,
    marginRight: 10,
  },
  resetButtonLabel: {
    fontSize: 16,
    color: "#2E2E2E",
  },
  resetButton: {
    margin: "8px !important",
    fontFamily: "DustinSans",
    height: "48px !important",
    width: "272px !important",
    border: "none !important",
    fontWeight: "bold !important",
    borderRadius: "4px !important",
    background: "none !important",
    display: "flex !important",
    alignItems: "center !important",
    "&:hover": {
      cursor: "pointer !important",
      background: "#F0F0F0 !important",
    },
    "&:active": {
      backgroundColor: "#E0E0E0 !important",
    },
  },
  resultSheet: {
    backgroundColor: "#2E2E2E !important",
    borderRadius: "8px 8px 0px 0px !important",
  },
});

const NO_DEVICES = {
  amountNotebook: 0,
  amountPeripheralDevice: 0,
  amountSmartphone: 0,
  amountTablet: 0,
  amountDesktop: 0,
  amountAllInOne: 0,
  amountMonitor: 0,
};

const App = () => {
  const classes = useStyles();

  const [amountNotebook, setAmountNotebook] = useState(0);
  const [amountPeripheralDevice, setAmountPeripheralDevice] = useState(0);
  const [amountSmartphone, setAmountSmartphone] = useState(0);
  const [amountTablet, setAmountTablet] = useState(0);
  const [amountDesktop, setAmountDesktop] = useState(0);
  const [amountAllInOne, setAmountAllInOne] = useState(0);
  const [amountMonitor, setAmountMonitor] = useState(0);
  const [cachedAmounts, setCachedAmounts] = useState(NO_DEVICES);
  const resultsRef = useRef(null);
  const inputsRef = useRef(null);

  const LL = locale.app;

  const resetDevices = () => {
    setAmountNotebook(0);
    setAmountPeripheralDevice(0);
    setAmountSmartphone(0);
    setAmountTablet(0);
    setAmountDesktop(0);
    setAmountAllInOne(0);
    setAmountMonitor(0);
    setCachedAmounts(NO_DEVICES);
  };

  const updateCache = () => {
    setCachedAmounts({
      amountNotebook: amountNotebook,
      amountPeripheralDevice: amountPeripheralDevice,
      amountSmartphone: amountSmartphone,
      amountTablet: amountTablet,
      amountDesktop: amountDesktop,
      amountAllInOne: amountAllInOne,
      amountMonitor: amountMonitor,
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.calculatorRoot}>
        <div className={classes.calculatorGrid}>
          <div className={classes.calculatorInputs}>
            <div ref={inputsRef} className={classes.calculatorDevices}>
              <DeviceCategory
                categoryName={LL.deviceCategories.notebook}
                icon={NotebookIcon}
                amount={amountNotebook}
                onAmountChange={(amount) => {
                  setAmountNotebook(amount);
                }}
              />
              <DeviceCategory
                categoryName={LL.deviceCategories.peripheralDevice}
                icon={PeripheralDeviceIcon}
                amount={amountPeripheralDevice}
                onAmountChange={(amount) => {
                  setAmountPeripheralDevice(amount);
                }}
                categoryInfo={LL.categoryInfos.peripheralDevice}
              />
              <DeviceCategory
                categoryName={LL.deviceCategories.smartphone}
                icon={SmartphoneIcon}
                amount={amountSmartphone}
                onAmountChange={(amount) => {
                  setAmountSmartphone(amount);
                }}
              />
              <DeviceCategory
                categoryName={LL.deviceCategories.tablet}
                icon={TabletIcon}
                amount={amountTablet}
                onAmountChange={(amount) => {
                  setAmountTablet(amount);
                }}
              />
              <DeviceCategory
                categoryName={LL.deviceCategories.desktop}
                icon={DesktopIcon}
                amount={amountDesktop}
                onAmountChange={(amount) => {
                  setAmountDesktop(amount);
                }}
              />
              <DeviceCategory
                categoryName={LL.deviceCategories.allInOne}
                icon={AllInOneIcon}
                amount={amountAllInOne}
                onAmountChange={(amount) => {
                  setAmountAllInOne(amount);
                }}
              />
              <DeviceCategory
                categoryName={LL.deviceCategories.monitor}
                icon={MonitorIcon}
                amount={amountMonitor}
                onAmountChange={(amount) => {
                  setAmountMonitor(amount);
                }}
              />
            </div>
            <div className={classes.calculatorCTARow}>
              <button
                disabled={
                  amountNotebook +
                    amountPeripheralDevice +
                    amountSmartphone +
                    amountTablet +
                    amountDesktop +
                    amountAllInOne +
                    amountMonitor ===
                  0
                }
                className={classes.calculateCTA}
                onClick={() => {
                  updateCache();
                  setTimeout(function () {
                    resultsRef &&
                      resultsRef.current &&
                      // scrollIntoView(resultsRef.current, {
                      //   behavior: "smooth",
                      //   block: "start",
                      //   inline: "nearest",
                      // });
                    resultsRef.current.scrollIntoView({
                      block: "start",
                      inline: "nearest",
                      behavior: "smooth",
                    });
                  }, 100);
                }}
              >
                <div className={classes.calculateCTALabel}>Calculate my impact</div>
                <img
                  src={CalculatorIcon}
                  alt={"calculator"}
                  className={classes.calculatorIcon}
                />
              </button>
              <button
                className={classes.resetButton}
                onClick={() => {
                  resetDevices();
                  inputsRef &&
                    inputsRef.current &&
                    // scrollIntoView(inputsRef.current, {
                    //   behavior: "smooth",
                    //   block: "end",
                    //   inline: "nearest",
                    // });
                    inputsRef.current.scrollIntoView({
                      block: "end",
                      inline: "nearest",
                      behavior: "smooth",
                    });
                }}
              >
                <img
                  src={ResetIcon}
                  alt={"reset"}
                  className={classes.resetIcon}
                />
                <div className={classes.resetButtonLabel}>Reset devices</div>
              </button>
            </div>
          </div>
        </div>

        <div ref={resultsRef} className={classes.resultSheet}>
          <div className={classes.calculatorGrid}>
            <CalculatorResults
              amountNotebook={cachedAmounts.amountNotebook}
              amountPeripheralDevice={cachedAmounts.amountPeripheralDevice}
              amountSmartphone={cachedAmounts.amountSmartphone}
              amountTablet={cachedAmounts.amountTablet}
              amountDesktop={cachedAmounts.amountDesktop}
              amountAllInOne={cachedAmounts.amountAllInOne}
              amountMonitor={cachedAmounts.amountMonitor}
            />
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

App.propTypes = {
  onReset: PropTypes.func.isRequired,
};

export default App;



// WEBPACK FOOTER //
// ./src/app/App.js